/**
 * InventoryCheckDialog
 * ====================
 * "Inventariar selección" — scan QR codes on printed labels to verify
 * that every item in the current filter selection is physically present.
 *
 * UX flow:
 *   1. Dialog opens with the full expected list + embedded camera.
 *   2. Every scan is decoded in real-time via jsQR at ~15 fps.
 *   3. Match   → ✅ check the item off + success beep.
 *      Mismatch → ❌ not-in-selection beep + brief error banner.
 *      Duplicate → ignored silently.
 *   4. "Finalizar" shows a summary: all found / missing / unexpected.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ReportProblem as WarningIcon,
} from '@mui/icons-material';
import type { InventorySelectionItem } from '../api/inventoryAdmin';

// ── Audio helpers (Web Audio API — no files needed) ────────────────────────

function beep(frequency: number, durationMs: number, volume = 0.4) {
  try {
    const ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = frequency;
    osc.type = 'sine';
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + durationMs / 1000);
  } catch {
    // Audio not available — ignore
  }
}

const successBeep = () => beep(880, 120);
const errorBeep = () => {
  beep(220, 80);
  setTimeout(() => beep(180, 120), 90);
};

// ── QR payload parser ──────────────────────────────────────────────────────
// Format: URBAN|<variantId>|<artId>|<sku>|<size>|<condition>

function parseQRPayload(raw: string): { variantId: number } | null {
  const parts = raw.split('|');
  if (parts[0] !== 'URBAN' || parts.length < 2) return null;
  const id = Number(parts[1]);
  if (!Number.isFinite(id) || id <= 0) return null;
  return { variantId: id };
}

// ── Types ──────────────────────────────────────────────────────────────────

type ScanPhase = 'scanning' | 'summary';

interface UnexpectedScan {
  variantId: number;
  rawData: string;
}

// ── Component ──────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  items: InventorySelectionItem[];
  onClose: () => void;
}

export function InventoryCheckDialog({ open, items, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);

  const [phase, setPhase] = useState<ScanPhase>('scanning');
  const [scannedIds, setScannedIds] = useState<Set<number>>(new Set());
  const [unexpected, setUnexpected] = useState<UnexpectedScan[]>([]);
  const [lastError, setLastError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [lastScanned, setLastScanned] = useState<InventorySelectionItem | null>(null);

  // Indexed map for O(1) lookup
  const itemMap = React.useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  // ── Camera lifecycle ────────────────────────────────────────────────────

  const stopCamera = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setCameraError(`No se pudo acceder a la cámara: ${msg}`);
    }
  }, []);

  // ── QR scan loop ────────────────────────────────────────────────────────

  // We need a stable ref to scannedIds and unexpected to avoid stale closures in rAF
  const scannedIdsRef = useRef<Set<number>>(new Set());
  const unexpectedRef = useRef<UnexpectedScan[]>([]);

  const scan = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scan);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      rafRef.current = requestAnimationFrame(scan);
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const qr = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });

    if (qr) {
      const parsed = parseQRPayload(qr.data);
      if (parsed) {
        const { variantId } = parsed;
        if (!scannedIdsRef.current.has(variantId)) {
          // New scan — process it
          if (itemMap.has(variantId)) {
            // ✅ In selection
            const next = new Set<number>();
            scannedIdsRef.current.forEach((id) => next.add(id));
            next.add(variantId);
            scannedIdsRef.current = next;
            setScannedIds(new Set(scannedIdsRef.current));
            setLastScanned(itemMap.get(variantId)!);
            setLastError(null);
            successBeep();
          } else {
            // ❌ Not in selection
            const alreadyUnexpected = unexpectedRef.current.some((u) => u.variantId === variantId);
            if (!alreadyUnexpected) {
              unexpectedRef.current = [...unexpectedRef.current, { variantId, rawData: qr.data }];
              setUnexpected([...unexpectedRef.current]);
            }
            setLastError(`ID ${variantId} no está en la selección actual`);
            setLastScanned(null);
            errorBeep();
          }
          // Throttle: pause rAF for 1s after a successful decode
          setTimeout(() => {
            rafRef.current = requestAnimationFrame(scan);
          }, 1000);
          return;
        }
        // Duplicate — throttle briefly
        setTimeout(() => {
          rafRef.current = requestAnimationFrame(scan);
        }, 500);
        return;
      }
    }

    // No QR detected — next frame at ~15fps (66ms)
    setTimeout(() => {
      rafRef.current = requestAnimationFrame(scan);
    }, 66);
  }, [itemMap]);

  // ── Effects ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (open && phase === 'scanning') {
      startCamera()
        .then(() => {
          rafRef.current = requestAnimationFrame(scan);
        })
        .catch(() => {});
    }
    return () => {
      stopCamera();
    };
  }, [open, phase, startCamera, scan, stopCamera]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setPhase('scanning');
      const fresh = new Set<number>();
      scannedIdsRef.current = fresh;
      setScannedIds(fresh);
      unexpectedRef.current = [];
      setUnexpected([]);
      setLastError(null);
      setLastScanned(null);
      setCameraError(null);
    }
  }, [open]);

  // ── Derived stats ────────────────────────────────────────────────────────

  const missingItems = items.filter((i) => !scannedIds.has(i.id));
  const progress = items.length > 0 ? (scannedIds.size / items.length) * 100 : 0;
  const allDone = scannedIds.size === items.length && unexpected.length === 0;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleFinish = () => {
    stopCamera();
    setPhase('summary');
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Inventariar selección
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {items.length} pares en la selección actual
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ── Progress bar ── */}
      <Box sx={{ px: 3, pt: 1.5, pb: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {scannedIds.size} / {items.length} escaneados
          </Typography>
          {unexpected.length > 0 && (
            <Typography variant="caption" color="error.main">
              {unexpected.length} fuera de selección
            </Typography>
          )}
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          color={allDone ? 'success' : 'primary'}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', gap: 2, pt: 1.5, pb: 1 }}>
        {phase === 'scanning' ? (
          <>
            {/* ── Left: Camera + feedback ── */}
            <Box sx={{ flex: '0 0 380px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {cameraError ? (
                <Alert severity="error">{cameraError}</Alert>
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#000',
                    aspectRatio: '4/3',
                    border:
                      // eslint-disable-next-line no-nested-ternary
                      lastError
                        ? '2px solid #ef4444'
                        : lastScanned
                          ? '2px solid #22c55e'
                          : '2px solid transparent',
                    transition: 'border-color 0.3s',
                  }}
                >
                  <video
                    ref={videoRef}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    muted
                    playsInline
                  />
                  {/* Scan crosshair overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        width: 180,
                        height: 180,
                        border: '2px solid rgba(255,255,255,0.6)',
                        borderRadius: 1,
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.25)',
                      }}
                    />
                  </Box>
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                </Box>
              )}

              {/* Last scan feedback */}
              {lastScanned && (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ fontWeight: 600 }}>
                  ✓ {lastScanned.productTitle}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {lastScanned.size} · {lastScanned.condition}
                    {lastScanned.artId ? ` · ${lastScanned.artId}` : ''}
                  </Typography>
                </Alert>
              )}
              {lastError && !lastScanned && <Alert severity="error">{lastError}</Alert>}

              <Button
                variant="contained"
                fullWidth
                onClick={handleFinish}
                sx={{
                  bgcolor: '#000',
                  '&:hover': { bgcolor: '#1e293b' },
                  fontWeight: 700,
                  textTransform: 'none',
                  mt: 'auto',
                }}
              >
                Finalizar inventario
              </Button>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* ── Right: Checklist ── */}
            <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Lista de selección
              </Typography>
              <List dense disablePadding>
                {items.map((item) => {
                  const checked = scannedIds.has(item.id);
                  return (
                    <ListItem
                      key={item.id}
                      disablePadding
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        opacity: checked ? 0.5 : 1,
                        bgcolor: checked ? 'success.50' : 'transparent',
                        transition: 'all 0.2s',
                        mb: 0.25,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {checked ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <UncheckedIcon color="disabled" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.productTitle}
                        secondary={`${item.size} · ${item.condition}${item.artId ? ` · ${item.artId}` : ''}`}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: checked ? 400 : 600,
                          noWrap: true,
                        }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </>
        ) : (
          /* ── Summary phase ── */
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {/* Result banner */}
            {missingItems.length === 0 && unexpected.length === 0 ? (
              <Alert severity="success" sx={{ mb: 3, fontWeight: 700, fontSize: 16 }}>
                🎉 Inventario completo — todos los {items.length} pares encontrados.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Inventario incompleto —{' '}
                {missingItems.length > 0 && `${missingItems.length} par(es) faltante(s)`}
                {missingItems.length > 0 && unexpected.length > 0 && ', '}
                {unexpected.length > 0 && `${unexpected.length} escaneo(s) fuera de selección`}.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* Found */}
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Typography fontWeight={700}>
                    Encontrados
                    <Badge badgeContent={scannedIds.size} color="success" sx={{ ml: 1.5 }} />
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {items
                    .filter((i) => scannedIds.has(i.id))
                    .map((item) => (
                      <ListItem key={item.id} disablePadding sx={{ px: 1, py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.productTitle}
                          secondary={`${item.size} · ${item.condition}`}
                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                </List>
              </Box>

              {/* Missing */}
              {missingItems.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <UncheckedIcon color="error" />
                    <Typography fontWeight={700} color="error.main">
                      Faltantes
                      <Badge badgeContent={missingItems.length} color="error" sx={{ ml: 1.5 }} />
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {missingItems.map((item) => (
                      <ListItem key={item.id} disablePadding sx={{ px: 1, py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <UncheckedIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.productTitle}
                          secondary={`${item.size} · ${item.condition}${item.artId ? ` · ${item.artId}` : ''}`}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 600,
                            noWrap: true,
                          }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Unexpected */}
              {unexpected.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WarningIcon color="warning" />
                    <Typography fontWeight={700} color="warning.dark">
                      Fuera de selección
                      <Badge badgeContent={unexpected.length} color="warning" sx={{ ml: 1.5 }} />
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {unexpected.map((u) => (
                      <ListItem key={u.variantId} disablePadding sx={{ px: 1, py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <WarningIcon color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`ID: ${u.variantId}`}
                          secondary="No pertenece a la selección actual"
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Chip
                          label="Ver"
                          size="small"
                          sx={{ ml: 1, cursor: 'pointer' }}
                          onClick={() =>
                            window.open(`/admin/inventory?sku=${u.variantId}`, '_blank')
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  // Re-scan: reset and go back to scanning phase
                  const fresh = new Set<number>();
                  scannedIdsRef.current = fresh;
                  setScannedIds(fresh);
                  unexpectedRef.current = [];
                  setUnexpected([]);
                  setLastError(null);
                  setLastScanned(null);
                  setPhase('scanning');
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Volver a escanear
              </Button>
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  bgcolor: '#000',
                  '&:hover': { bgcolor: '#1e293b' },
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Cerrar
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InventoryCheckDialog;
